<template>
  <v-container fluid>
    <div v-if="user.can('view_sustainability')">
      <div class="d-flex align-center">
        <h2 class="mb-0">
          {{ headerText }}
        </h2>
        <div class="mr-auto">
          <info-modal>
            <template v-slot:button="{ on, attrs }">
              <v-btn icon color="primary" v-bind="attrs" v-on="on">
                <v-icon large>mdi-information-outline</v-icon>
              </v-btn>
            </template>
            <v-card-title>
              <h4>{{ $t('sustainability_dashboard_ui.modal_info.vehicles_with_high_reefer_activity.title') }}</h4>
            </v-card-title>
            <v-card-text v-html="$t('sustainability_dashboard_ui.modal_info.vehicles_with_high_reefer_activity.text')"/>
          </info-modal>
        </div>
        <sustainability-settings/>
      </div>
      <div class="mb-2 d-flex justify-content-between">
        <h4>{{ selectedCustomer.name }} <span class="small ml-2 text-muted">({{ this.summaryString }})</span></h4>
        <v-btn size="sm" @click="backToDashboard()">
          <v-icon>mdi-arrow-left</v-icon>
          {{ $t('sustainability_dashboard_ui.back_to_dashboard') }}
        </v-btn>
      </div>
      <v-sheet outlined rounded class="px-4 py-2">
        <div class="text-subtitle-1">{{ $t('filter_by') }}</div>
        <v-row dense>
          <v-col lg="4" md="6" cols="12">
            <v-select
              small
              :label="$t('minimum_number_of_routes')"
              v-model="searchCriteria.minNumRoutes"
              :items="minNumberRouteOptions"
              prepend-inner-icon="mdi-filter"
              @change="initiateSearch"
            />
          </v-col>
        </v-row>
        <div class="d-flex justify-end mt-1 mb-2">
          <v-btn outlined color="primary" @click="initiateSearch()" >
            <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
            {{ $t('search') }}
          </v-btn>
          <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
            <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
            {{ $t('reset') }}
          </v-btn>
        </div>
      </v-sheet>
      <div class="d-flex justify-space-between align-center mt-2">
        <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('result', 2) }}</div>
        <table-pagination-controls
          :total-rows="remoteData.totalRows"
          :current-page.sync="remoteData.currentPage"
          :rows-per-page.sync="remoteData.rowsPerPage"
          @update:currentPage="refreshResults"
          @update:rowsPerPage="initiateSearch"
          :per-page-options="remoteData.pageOptions"/>
      </div>
      <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
        <v-data-table
          hide-default-footer
          class="vf-table mt-2"
          :headers="tableFields"
          :items="remoteData.items"
          :server-items-length="remoteData.totalRows"
          :sort-by.sync="remoteData.sortBy"
          :sort-desc.sync="remoteData.sortDesc"
          :loading="remoteData.isBusy"
          :loading-text="$t('loading')"
          @update:sort-by="initiateSearch()"
          @update:sort-desc="initiateSearch()">

          <template v-slot:header="props">
            <thead>
            <tr>
              <th colspan="1"><span class="sr-only">{{ $tc('vehicle') }}</span></th>
              <th colspan="1" variant="standard" class="text-center">
                {{ $t('average_diesel_runtime') }}
                {{ formatNumber(averageDieselPct, 1) }}%
                <br><small>{{ $t('average_diesel_runtime_desc') }}</small>
              </th>
              <th colspan="1"><span class="sr-only">{{ $tc('vehicle') }}</span></th>
              <th colspan="3" variant="standard" class="text-center">
                {{ $t('potential_savings') }}
                <br><small>{{ $t('potential_savings_desc') }}</small>
                <vf-tooltip :text="$t('potential_savings_desc_tooltip')">
                  <v-icon>mdi-information</v-icon>
                </vf-tooltip>
              </th>
            </tr>
            </thead>
          </template>

          <template v-slot:header.power_diesel_pct="{ header }">
            {{ $t('average_runtime') }}
            <vf-tooltip :text="$t('average_runtime_tooltip')">
              <v-icon>mdi-information</v-icon>
            </vf-tooltip>
          </template>

          <template v-slot:item.vehicle_name="row">
            <router-link :to="`/vehicle/${row.item.vehicle_id}`">
              <font-awesome-icon class="mr-1" fixed-width icon="truck"/>
              {{ row.item.vehicle_name }}
            </router-link>
          </template>

          <template v-slot:item.power_diesel_pct="row">
            <if-set :value="row.value">
              <template>{{ formatNumber(row.value, 1) }}%</template>
            </if-set>
          </template>

          <template v-slot:item.num_routes="row">
            {{ row.value }}
          </template>

          <template v-slot:item.fuel_quantity_savings="row">
            <if-set :value="row.value">
              <template>{{ hideNegativeSavings(formatNumber(row.value, 1), row) }}</template>
            </if-set>
          </template>

          <template v-slot:item.currency_savings="row">
            <if-set :value="row.value">
              <template>{{ hideNegativeSavings(formatCurrency(row.value, currencySymbol), row) }}</template>
            </if-set>
          </template>

          <template v-slot:item.co2_savings="row">
            <if-set :value="row.value">
              <template>{{ hideNegativeSavings(formatNumber(row.value, 1), row) }}</template>
            </if-set>
          </template>
        </v-data-table>
      </div>
    </div>
    <div v-else v-html="$t('404_error_message' )"/>
  </v-container>
</template>

<script>
import SustainabilitySettings from '@/components/sustainability/SustainabilitySettings'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import SustainabilityMixin from '@/components/mixins/SustainabilityMixin'
import TablePaginationControls from '@/components/shared/TablePaginationControls.vue'
import InfoModal from '@/components/shared/InfoModal'
import { map, round, find } from 'lodash'
import Moment from 'moment-timezone'
import VfTooltip from '@/components/shared/VfTooltip.vue'

export default {
  name: 'HighReeferActivityVehicles',
  components: {
    VfTooltip,
    SustainabilitySettings,
    TablePaginationControls,
    InfoModal
  },
  mixins: [
    CurrentUserMixin,
    RemoteDataTableMixin,
    ApiUtilitiesMixin,
    DateTimeUtilsMixin,
    SustainabilityMixin
  ],
  data () {
    return {
      model: 'HighReeferActivityVehicle',
      averageDieselPct: null,
      vehicles: [],
      searchCriteria: this.getDefaultSearchCriteria(),
      sortBy: 'over_average',
      sortDesc: true,
      rowsPerPage: 100,
      minNumberRouteOptions: [
        { value: 0, text: 'All' },
        { value: 5, text: '5' },
        { value: 10, text: '10' },
        { value: 15, text: '15' },
        { value: 20, text: '20' },
        { value: 25, text: '25' }
      ]
    }
  },
  methods: {
    /** small func to navigate back to dashboard, using a 'to' attribute makes the button look funny for some reason */
    backToDashboard () {
      this.$router.push('/sustainability/customers/' + this.selectedCustomer.id)
    },
    /**
     * A little trick method to hide certain fields when the savings would be zero or negative (because the vehicle has
     * equal or lower usage compared to the target)
     * @param value
     * @param row
     */
    hideNegativeSavings (value, row) {
      if (row.item.over_average <= 0) {
        return null
      }
      return value
    },
    /**
     * Convenience function to return formatted string of number parameter
     * @param value
     * @param precision
     * @returns {string}
     */
    formatNumber (value, precision) {
      return round(value, precision).toFixed(precision).toLocaleString()
    },
    /**
     * Sets our data table back to its original state before the user applied filtering and paging changes.
     * @return undefined
     */
    resetTable () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      this.resetRemoteDataTableNavigation()
      this.refreshResults()
    },

    /**
     * Returns the default search criteria for this data table
     * @return Object
     */
    getDefaultSearchCriteria () {
      return {
        name: null,
        vehicles: [],
        startedAfter: null,
        startedBefore: null,
        overAverage: null,
        minNumRoutes: 10
      }
    },

    fetchVehicles () {
      this.$orm('Vehicle')
        .findAll({ customer_id: this.selectedCustomer.id, sort: 'name', 'per-page': 0 })
        .then((response) => {
          this.vehicles = response.getItems()
        })
        .catch(error => {
          if (error) {
            this.renderErrorFlashBanner(error)
            throw (error)
          }
        })
    },

    /**
     * Constructs an object of key value pairs that represent the current search
     * criteria
     *
     * @return object
     */
    buildSearchCriteria () {
      const searchCriteria = {
        customer_id: this.selectedCustomer.id,
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage,
        power_diesel_avg: this.averageDieselPct,
        strategy: this.strategy
        // By default, we want to filter to routes where the diesel power was over the average we retrieved earlier.
      }

      // Filter by vehicle (if specified)
      if (this.searchCriteria.vehicles.length > 0) {
        searchCriteria.vehicle_id = map(this.searchCriteria.vehicles, 'value').join(',')
      }

      // Attach the over average value (if specified)
      if (this.searchCriteria.overAverage != null) {
        // If we specify this value, we want to see routes where the diesel power percentage was the average by
        // the specified amount (so add the specified value to the average value)
        searchCriteria.power_diesel_pct_over = this.searchCriteria.overAverage + this.averageDieselPct
      }

      if (this.remoteData.sortBy != null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }

      if (this.sustainabilityParams.timeRange !== 'custom') {
        searchCriteria.start_after = this.sustainabilityParams.timeRange
        searchCriteria.start_before = 'now'
      } else {
        searchCriteria.start_after = Moment.tz(this.sustainabilityParams.startDate, this.customerTimezone).format()
        searchCriteria.start_before = Moment.tz(this.sustainabilityParams.endDate, this.customerTimezone).format()
      }

      if (this.searchCriteria.minNumRoutes) {
        searchCriteria.min_num_routes = this.searchCriteria.minNumRoutes
      }
      return searchCriteria
    },

    /**
     * Adapt the remote data into a format suitable for display in the table. This includes adding a couple new fields
     * generated from the existing data.
     *
     * @param items
     */
    transformRemoteData (items) {
      return map(items, item => {
        const percentOver = item.power_diesel_pct - this.averageDieselPct
        item.over_average = percentOver
        item.fuel_quantity_savings = this.convertDieselFromLiters(item.potential_runtime_savings)
        item.currency_savings = this.sustainabilityParams.dieselPrice * item.fuel_quantity_savings
        item.co2_savings = this.getCO2Emissions(item.potential_runtime_savings)
        return item
      })
    },

    refreshResults () {
      this.remoteData.isBusy = true
      this.loadRemoteDataDelayed()
    },

    /**
     * Load data from the API based on the current search criteria.
     * @return Array|null
     */
    async loadRemoteData () {
      this.remoteData.isBusy = true
      try {
        const searchCriteria = this.buildSearchCriteria()
        const collection = await this.$orm(this.model).findAll(searchCriteria)
        this.remoteData.currentPage = collection.getCurrentPage()
        this.remoteData.totalRows = collection.getTotalItemCount()
        this.remoteData.items = this.transformRemoteData(collection.getItems())
        if (this.strategy === 'enroute') {
          this.$httpAdapter.get('/sustainability-insights/average-enroute-diesel-activity', { params: searchCriteria })
            .then(response => {
              this.averageDieselPct = response.data.average_diesel_pct
            })
        } else {
          this.$httpAdapter.get('/sustainability-insights/average-staging-diesel-activity', { params: searchCriteria })
            .then(response => {
              this.averageDieselPct = response.data.average_diesel_pct
            })
        }
      } catch (error) {
        await this.$store.dispatch('globalMessagingModule/showModal', {
          message: this.$t('network_error_message')
        })
        throw (error)
      } finally {
        this.remoteData.isBusy = false
        this.remoteData.isLoaded = true
      }
    }
  },
  computed: {
    /**
     * Get the currently selected customer from route parameters, or use the current customer context.
     */
    selectedCustomer: {
      get () {
        const customerId = parseInt(this.$route.query.customer_id)
        // If no customers specified, default to the current customer context
        if (!Number.isInteger(customerId)) {
          return this.customerContext
        }
        return find(this.customers, x => x.id === customerId)
      }
    },
    strategy () {
      return this.$route.query.strategy
    },

    headerText () {
      const strategy = this.strategy
      if (strategy === 'enroute') {
        return this.$t('sustainability_dashboard_ui.vehicles_with_high_reefer_activity')
      } else {
        return this.$t('sustainability_dashboard_ui.vehicles_with_high_staging_activity')
      }
    },
    /**
     * @return Array
     */
    tableFields () {
      const fields = []

      fields.push({ value: 'vehicle_name', text: this.$tc('vehicle'), sortable: true })
      fields.push({ value: 'power_diesel_pct', text: this.$t('average_runtime'), sortable: true })
      fields.push({ value: 'num_routes', text: this.$t('number_of_routes'), sortable: true })
      fields.push({ value: 'fuel_quantity_savings', text: this.$tc(this.dieselUnitLabel, 2), sortable: true })
      fields.push({ value: 'currency_savings', text: this.sustainabilityParams.currency, sortable: true })
      fields.push({ value: 'co2_savings', text: this.weightFormatLabel, sortable: false })

      return fields
    }

    /** Commenting out for now.
     vehicleNames () {
      return sortBy(map(this.vehicles, v => {
        return { text: v.name, value: v.id }
      }))
    } */
  },
  mounted () {
    this.initiateSearch()
  },
  watch: {
    sustainabilityParams () {
      this.refreshResults()
    }
  }
}
</script>

<style scoped lang="scss">

</style>
